import { RELATIONSHIP_TYPE_CHILD, RELATIONSHIP_TYPE_SELF } from '~/constants';
import { analyticsTrackEvent } from '~/core/analytics';
import { SOLV_PLUS_ENTRYPOINT_TAPPED } from '~/core/analytics/events';
import { UserProfile } from '~/core/dapi/models';
import history from '~/core/history';
import { isActiveMembership } from '~/core/util/account';
import { isRedirectingAfterLogin } from '~/core/util/url';
import { useAccount } from '~/hooks/account/useAccount';
import useToggleRuntimeModal from '~/hooks/runtime/useToggleRuntimeModal';
import { MEMBERSHIP_SUPPORTED_RELATIONSHIPS } from './constants';
import { MEMBERSHIP_PRODUCT_MODAL } from './ProductModal';

/**
 * Handles when a solv plus entrypoint is clicked, directing to the proper page
 * based on whether the user has a membership.
 *
 * @param entrypoint An entrypoint string to use in analytics tracking
 *
 * @returns an onClick handler and membership state
 */
export function useOnMembershipEntrypointClicked(
  entrypoint: string,

  {
    returnToCurrentUrl = true,
    directToPayment = false,
  }: { returnToCurrentUrl?: boolean; directToPayment?: boolean } = {}
) {
  const accountSummary = useAccount();
  const isMember = isActiveMembership(accountSummary);

  const [_, toggle] = useToggleRuntimeModal(MEMBERSHIP_PRODUCT_MODAL);

  const onClick = () => {
    analyticsTrackEvent(SOLV_PLUS_ENTRYPOINT_TAPPED, {
      entrypoint,
      isMember,
    });
    if (isActiveMembership(accountSummary)) {
      history.push(
        `/book${
          returnToCurrentUrl && `?return=${window.location.pathname}${window.location.search}`
        }`
      );
    } else {
      if (directToPayment) {
        history.push('/membership/payment');
      } else {
        toggle();
      }
    }
  };

  return { onClick, isMember };
}

/**
 * Whether the user is on a login page and about to be redirect to a Solv Plus route
 *
 * @returns bool
 */
export function isRedirectingToSolvPlus() {
  return isRedirectingAfterLogin({ to: [/\/book/, /\/membership\/payment/] });
}

const lowercaseRelationships = MEMBERSHIP_SUPPORTED_RELATIONSHIPS.map((s) => s.toLowerCase());
/**
 * Whether a given user profile has the right relationship to an account to be
 * supported by solv plus
 *
 * @returns boolean
 */
export function profileSupportsMembership(userProfile?: UserProfile) {
  // initial profiles do not have a "relationship" set
  if (userProfile?.relationship_to_account === null) return true;

  return lowercaseRelationships.includes(userProfile?.relationship_to_account?.toLowerCase() ?? '');
}
